import { LineChartOutlined } from '@ant-design/icons';
import { BestOfLake, BestOfTopBait } from '@omniafishing/core';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ContentWrapper } from '../../components/content_wrapper/content_wrapper';
import { OmniaLinkButton } from '../../components/omnia_button/omnia_link_button';
import { ScrollToTop } from '../../components/scroll_to_top/scroll_to_top';
import { SEO } from '../../components/seo/seo';
import { Spacer } from '../../components/spacer/spacer';
import { TopWaterbodies } from '../../components/state_detail/top_waterbodies';
import SvgFire from '../../components/svg/fire';
import { UserProfileImg } from '../../components/user_profile_img/user_profile_img';
import { useListViewed } from '../../hooks/use_list_viewed';
import { useQueryString } from '../../hooks/use_query_string';
import { useUserPreferences } from '../../hooks/use_user_preferences';
import { GlobalQueryParams } from '../../lib/query_string';
import { OmniaUrls } from '../../lib/urls';
import { waterbodyToWaterbodyDetailLossy } from '../../lib/waterbody';
import { WebAnalytics } from '../../lib/web_analytics';
import { getSpecies, getTechniques } from '../../redux/reference_data';
import { RoutePaths } from '../../routes';
import { GetStartedNewsletterModal } from '../get_started/get_started_newsletter_modal/get_started_newsletter_modal';
import { AboutOmniaBlurb } from './about_omnia_blurb';
import styles from './best_of_year_lake_page.less';
import { previousYearPlusOneMonth } from './best_of_year_page';

export const BestOfYearLakePage = (props: { best_of_lake: BestOfLake }) => {
  const { best_of_lake } = props;
  const allSpecies = useSelector(getSpecies);
  const allStyles = useSelector(getTechniques);
  let topBaits = [] as BestOfTopBait[];
  const { coupon, createListId } = useListViewed();
  const { setUserPreferencesWaterbody } = useUserPreferences();
  const { getCurrentQuery } = useQueryString();
  const currentQuery = getCurrentQuery<{ hide_year: boolean }>();

  const { waterbody, state, state_top_waterbodies, top_baits } = best_of_lake;
  topBaits = top_baits;
  const topBaitSpeciesNames = _.uniq(topBaits.map((tb) => tb.species));
  const topSpecies = allSpecies.find((s) => s.name === topBaitSpeciesNames[0]);
  const topBaitStyleNames = _.uniq(topBaits.map((tb) => tb.style));
  const topTechniques = topBaitStyleNames
    .map((ts) => allStyles.find((s) => s.name === ts))
    .slice(0, 3);

  useEffect(() => {
    WebAnalytics.productFamilyListViewed({
      coupon,
      productFamilies: topBaits.map((tb) => tb.product_family),
      list_id: createListId('other'),
    });
  }, [topBaits.map((tb) => tb.product_family.handle)]);

  useEffect(() => {
    setUserPreferencesWaterbody(waterbodyToWaterbodyDetailLossy(waterbody));
  }, []);

  const showYear = !currentQuery.hide_year;
  const showYearHelper = showYear ? previousYearPlusOneMonth : '';

  const SEODescription = `A list of the best baits and lures for ${
    waterbody.primary_name
  } fishing reported by local anglers${showYear ? `in ${previousYearPlusOneMonth}` : ''}.`;

  return (
    <ScrollToTop>
      <SEO
        title={`Top ${waterbody.primary_name} Fishing Tackle ${showYearHelper} Report`}
        description={SEODescription}
      />
      <div className={styles.page}>
        <ContentWrapper className={styles.crumbWrapper}>
          <div className={styles.breadcrumbs}>
            <div className={styles.back}>
              <OmniaLinkButton kind="text" size="md" to={RoutePaths.BEST_OF_YEAR}>
                {showYear ? `< Back to Best of ${previousYearPlusOneMonth}` : '< Back'}
              </OmniaLinkButton>
            </div>
          </div>
        </ContentWrapper>
        <header className={styles.header}>
          <ContentWrapper>
            <h1 className={styles.h1}>Top {waterbody.primary_name} Fishing Tackle</h1>
            <p>
              At Omnia Fishing, we collect thousands of fishing reports from local anglers who share
              what they caught and how they caught it.
              {showYear
                ? `Here are the most successful baits reported in ${waterbody.primary_name} in ${previousYearPlusOneMonth}.`
                : `Here are the most successful baits reported in ${waterbody.primary_name}.`}
            </p>
            <p className={styles.termina}>
              <Link to={OmniaUrls.waterbody(waterbody)}>View all</Link> fishing reports and lake
              information for {waterbody.primary_name}
            </p>
          </ContentWrapper>
        </header>
        <Spacer size={48} />
        <ContentWrapper>
          <section>
            <h2 className={styles.h2}>
              {showYearHelper} Hot Bait Report—{waterbody.primary_name}
            </h2>
            <p style={{ textAlign: 'center' }}>
              {topSpecies.display_name} was the top species targeted this year. Local anglers
              reported these favorite techniques:
            </p>
            <ul className={styles.stylesList}>
              {topTechniques.map((t) => (
                <li key={t.name}>
                  <OmniaLinkButton kind="link" size="md" to={OmniaUrls.technique(t)}>
                    <img src={t.image} alt={t.img_alt} width={50} height={50} />{' '}
                    {t.display_name_alternate}
                  </OmniaLinkButton>
                </li>
              ))}
            </ul>

            <Spacer size={36} />

            <section>
              <ul className={styles.topBaits}>
                {topBaits.map((top_bait) => {
                  const species = allSpecies.find((s) => s.name === top_bait.species);
                  return (
                    <li className={styles.topBaitItem} key={top_bait.product_family.handle}>
                      <div className={styles.topBait}>
                        <header className={styles.topBaitHeader}>
                          <div className={styles.topBaitFire}>
                            <SvgFire className={styles.topBaitIcon} />
                            {showYearHelper}
                          </div>
                          <div className={styles.topBaitText}>
                            <p className={styles.topBaitLake}>{waterbody.primary_name} Hot Bait</p>
                            <span>For {species.display_name}</span>
                          </div>
                        </header>
                        <div className={styles.topBaitImg}>
                          <Link to={OmniaUrls.productFamily(top_bait.product_family.handle)}>
                            <img src={top_bait.product_family.img_url} />
                          </Link>
                        </div>
                        <div className={styles.topBaitTitle}>
                          <Link to={OmniaUrls.productFamily(top_bait.product_family.handle)}>
                            {top_bait.product_family.title}
                          </Link>
                        </div>
                        {top_bait.fishing_reports.length > 0 ? (
                          <>
                            <div className={styles.topBaitReports}>
                              {top_bait.fishing_reports.map((report) => (
                                <Link
                                  to={
                                    OmniaUrls.waterbody(report.waterbody) +
                                    `?${GlobalQueryParams.report_id}=${report.id}`
                                  }
                                  rel="noopener nofollow"
                                  key={report.id}
                                >
                                  <UserProfileImg
                                    image={report.user.image}
                                    imageSize={60}
                                    size={40}
                                  />
                                </Link>
                              ))}
                            </div>
                            <p>
                              <Link
                                to={OmniaUrls.waterbody(waterbody)}
                                className={styles.topBaitLink}
                              >
                                View Fishing Reports
                              </Link>
                            </p>
                          </>
                        ) : (
                          <>
                            <div className={styles.featuredBait}>
                              <LineChartOutlined className={styles.featuredBaitIcon} />
                              <span>Featured Bait for {species.display_name}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>

              <Spacer size={48} />
            </section>
          </section>
        </ContentWrapper>

        {/* <section className={styles.trends}>
          <ContentWrapper>
            <h2 className={styles.h2}>{previousYearPlusOneMonth} Tackle Industry Trends</h2>
            <p>
              Some fishing trends were significant enough to show up across the nation. Get the
              scoop on the biggest topics and emerging trends for the year.
            </p>
            <Link to={`${RoutePaths.OMNIA_VIDEOS}/omnia-best-of-${previousYearPlusOneMonth}`}>
              <img
                src={thumbnail}
                alt={`Omnia's Best of ${previousYearPlusOneMonth}`}
                className={styles.video}
              />
            </Link>
          </ContentWrapper>
        </section> */}

        <ContentWrapper>
          <section className={styles.aboutOmnia}>
            <h3 className={styles.h2}>About Omnia Fishing</h3>
            <AboutOmniaBlurb />
            <TopWaterbodies
              topWaterbodies={state_top_waterbodies.map((w) => ({
                waterbody: w,
                fishing_reports_count: 0,
                recent_fishing_reports_count: 0,
              }))}
              stateName={_.startCase(state.name)}
              bbox={state.bbox}
            />
          </section>
        </ContentWrapper>
      </div>

      <GetStartedNewsletterModal context="best_of" />
    </ScrollToTop>
  );
};
